import { css, html, TemplateResult, unsafeCSS } from "lit";
import { customElement } from "lit/decorators/custom-element.js";
import "./../atoms/title";
import "./../atoms/text";
import "./../atoms/media";
import "./../atoms/markdown";
import { property } from "lit/decorators/property.js";
import { highlightText, Idea, MediaTransformationKind, truncateString } from "shared";
import { BsCard } from "../atoms/card";
import { when } from "lit/directives/when.js";
import { repeat } from "lit/directives/repeat.js";
import { classMap } from "lit/directives/class-map.js";
import { openLightBox } from "../dialogs/open-light-box";
import { asyncModel } from "../util/async-model-directive";
import { EMOJI_FONT_NAME } from "../config";

@customElement("bs-idea")
export class BsIdea extends BsCard {
	static get styles() {
		return [
			...super.styles,
			css`
				:host {
					--user-avatar-size: 22px;
				}

				:host([size="large"]) {
					--card-padding: var(--space-l);
					--user-avatar-size: 28px;
				}

				:host([size="large"]) #media-container {
					height: 250px;
				}

				#text {
					white-space: pre-wrap;
				}

				#content {
					display: flex;
					gap: var(--space-s);
				}

				#idea-content {
					display: flex;
					flex-direction: column;
					gap: var(--space-s);
					width: 100%;
					overflow: hidden;
				}

				#media-container {
					display: flex;
					gap: var(--space-m);
					overflow-x: auto;
					height: 150px;
				}

				#media-container:not(.fill) {
					padding: var(--space-s);
					margin: calc(-1 * var(--space-s)) calc(-1 * var(--space-s)) 0;
				}

				.media {
					min-width: 200px;
				}

				#footer,
				::slotted([slot="footer"]) {
					border-top: 1px solid var(--shade-normal);
					padding: var(--space-s) 0 0;
					justify-content: flex-start;
				}

				#reactions {
					align-self: flex-start;
					line-height: 1;
					font-size: var(--font-size-s);
					--card-group-height: 30px;
					--card-group-item-padding: var(--space-s);
				}

				.reaction {
					display: flex;
					gap: var(--space-xs);
					align-items: center;
				}

				.symbol {
					font-family: "${unsafeCSS(EMOJI_FONT_NAME)}";
				}
			`
		];
	}

	@property({ type: Object }) idea!: Idea;
	@property({ type: String, attribute: "media-transformation-kind" }) mediaTransformationKind: MediaTransformationKind =
		"normal";
	@property({ type: Boolean, reflect: true }) zoomable = false;
	@property({ type: Boolean, reflect: true }) anonymous = false;
	@property({ type: Boolean, reflect: true, attribute: "hide-reactions" }) hideReactions = false;
	@property({ type: String }) query: string | undefined;
	@property({ type: Number }) truncate: number | undefined;

	renderFooter(): TemplateResult | undefined {
		return html`<slot name="footer"></slot>`;
	}

	render() {
		const { idea, zoomable } = this;
		const text = this.truncate != null ? truncateString(idea.text ?? "", this.truncate) : idea.text;
		return html`
			<div id="content">
				${when(
					!this.anonymous,
					() => html`
						<div id="info">
							${asyncModel(
								idea.createdBy,
								(user) =>
									html`
										<bs-user-avatar id="user" .user="${user}" ?ai-assisted="${idea.isAIAssisted}"></bs-user-avatar>
									`
							)}
						</div>
					`
				)}
				<div id="idea-content">
					${when(
						idea.text,
						() => html`
							<bs-markdown
								size="${this.size}"
								text="${this.query ? highlightText({ text: text, query: this.query }) : text}"
							></bs-markdown>
						`
					)}
					${when(
						idea.media != null && idea.media.length > 0,
						() => html`
							<div
								id="media-container"
								class="${classMap({
									fill: idea.media?.length === 1
								})}"
							>
								${repeat(
									idea.media ?? [],
									(media, i) =>
										html`
											<bs-media
												class="media"
												.media="${media}"
												?zoomable="${zoomable}"
												@zoom="${() =>
													openLightBox({
														attachments: idea.media ?? [],
														index: i
													})}"
												transformation-kind="${this.mediaTransformationKind}"
											></bs-media>
										`
								)}
							</div>
						`
					)}
					${when(
						!this.hideReactions && idea.reactions != null && Object.values(idea.reactions).length > 0,
						() => html`
							<bs-card-group flat id="reactions">
								${repeat(
									Object.entries(idea.reactions ?? {}).sort(([, countA], [, countB]) => countB - countA),
									([reaction, count]) => html`
										<bs-card-group-item class="reaction">
											<span class="symbol">${reaction}</span>
										</bs-card-group-item>
									`
								)}
							</bs-card-group>
						`
					)}
					${this.renderFooter()}
				</div>
			</div>
		`;
	}
}
